// client/pages/Swap.tsx - Updated with 24H button and transparent selection

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { useTheme } from "@/contexts/ThemeContext";
import { useSolanaBalances } from "@/hooks/use-solana-balances";
import { ArrowLeft, ArrowUpDown, Loader2, ExternalLink, Clock, History, CheckCircle, X, ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Copy, BarChart3, Info, Upload, MoreVertical } from "lucide-react";
import { cn } from "@/lib/utils";

// ============ HELPER FUNCTIONS ============
const extractHashFromMessage = (message: string): string => {
  const match = message.match(/([a-zA-Z0-9]{44,})/);
  return match ? match[1] : message;
};

// ============ SOLANA TOKEN CONFIGURATION ============
const TOKENS_LIST = ["SOL", "USDT", "USDC", "FIXERCOIN", "LOCKER", "PINGX", "FXM"];

// Tokens to show in marquee and left column (excluding USDC, USDT)
const DISPLAY_TOKENS_LIST = ["SOL", "FIXERCOIN", "LOCKER", "PINGX", "FXM"];

const TOKEN_ADDRESSES: Record<string, string> = {
  SOL: "So11111111111111111111111111111111111111112",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
  LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
  PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
  FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
};

const SOL_ADDRESS = "So11111111111111111111111111111111111111112";

const TOKEN_DECIMALS: Record<string, number> = {
  SOL: 9,
  USDT: 6,
  USDC: 6,
  FIXERCOIN: 6,
  LOCKER: 6,
  PINGX: 6,
  FXM: 6,
};

const MIN_SMALLEST_UNITS = 1000;
const getMinimumAmount = (tokenSymbol: string): number => {
  const decimals = TOKEN_DECIMALS[tokenSymbol] || 9;
  return MIN_SMALLEST_UNITS / Math.pow(10, decimals);
};

// Fee configuration - Only Solana address for platform fees
const PLATFORM_FEE_PERCENT = 1; // 1%
const FEE_ADDRESS = "9PsJzqtusfNNEBNMF7AoTE5YuU7CBSMW8ccoPan15MDd";

// ============ SLIPPAGE CONFIGURATION ============
const DEFAULT_SLIPPAGE_PERCENT = 1;
const DEFAULT_SLIPPAGE_BPS = DEFAULT_SLIPPAGE_PERCENT * 100;

interface SwapQuote {
  inputAmount: number;
  outputAmount: number;
  price: number;
  priceImpact: number;
  slippageUsed?: number;
}

// ============ BIRDEYE CHART COMPONENT - DYNAMIC THEME ============
const BirdeyeChart = ({ tokenAddress, chain }: { tokenAddress: string; chain: string }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!tokenAddress || !chartRef.current) return;

    setIsLoading(true);

    // Determine theme and colors based on dark mode
    const isDark = isDarkMode;
    const theme = isDark ? 'dark' : 'light';
    const backgroundColor = isDark ? '%23000000' : '%23FFFFFF';
    const textColor = isDark ? '%23FFFFFF' : '%23111A28';
    const borderColor = isDark ? '%233F3F46' : '%23E5E7EB';
    
    // Chart colors (keeping green/red for candles regardless of theme)
    const upColor = '%2322C55E';
    const downColor = '%23EF4444';
    
    const widgetUrl = `https://embed.birdeye.so/tv-widget/${tokenAddress}?chain=${chain}&viewMode=pair&chartInterval=1D&chartType=Candle&chartTimezone=Asia%2FKarachi&chartLeftToolbar=hide&theme=${theme}&backgroundColor=${backgroundColor}&textColor=${textColor}&borderColor=${borderColor}&chartOverrides=mainSeriesProperties.candleStyle.downColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.borderDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.wickDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.upColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.borderUpColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.wickUpColor=${upColor}`;

    const iframe = document.createElement('iframe');
    iframe.src = widgetUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '12px';
    iframe.style.background = 'transparent';
    iframe.allow = 'clipboard-write';
    iframe.onload = () => {
      setIsLoading(false);
    };

    chartRef.current.innerHTML = '';
    chartRef.current.appendChild(iframe);

    return () => {
      if (chartRef.current) {
        chartRef.current.innerHTML = '';
      }
    };
  }, [tokenAddress, chain, isDarkMode]);

  return (
    <div className="bg-card border border-border rounded-xl p-4 h-full flex flex-col shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground">
            {Object.keys(TOKEN_ADDRESSES).find(key => TOKEN_ADDRESSES[key] === tokenAddress) || 'Token'} / SOL
          </h3>
          <p className="text-[8px] text-muted-foreground uppercase">Birdeye Chart</p>
        </div>
        <div className="flex gap-1">
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">1H</button>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">24H</button>
          <button className="text-[8px] text-yellow-500 uppercase px-2 py-0.5 border border-yellow-500 rounded">7D</button>
        </div>
      </div>
      
      <div className="flex-1 relative min-h-[300px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
          </div>
        )}
        <div ref={chartRef} className="w-full h-full rounded-lg overflow-hidden" />
      </div>
    </div>
  );
};

// ============ LEFT COLUMN - TOKEN PRICES (TRANSPARENT WITH 24H BUTTON) ============
const TokenPriceList = ({ prices, onSelectToken, selectedToken }: any) => {
  const formatPrice = (symbol: string, price: number) => {
    if (!price || price === 0) return "$0.00";
    if (symbol === "SOL") {
      return `$${price.toFixed(2)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  const formatChange = (change: number) => {
    if (!change) return "0.00%";
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  };

  return (
    <div className="bg-transparent border border-border rounded-xl p-4 h-full flex flex-col overflow-hidden">
      <div className="border-b border-border pb-2 mb-2 flex-shrink-0">
        <div className="flex justify-between items-center">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-yellow-500">Token Prices</h3>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">
            24H Change
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="space-y-1">
          {DISPLAY_TOKENS_LIST.map((symbol) => {
            const priceData = prices[symbol];
            const price = priceData?.price || 0;
            const change = priceData?.change || 0;
            const isSelected = selectedToken === symbol;

            return (
              <button
                key={symbol}
                onClick={() => onSelectToken(symbol)}
                className={cn(
                  "w-full flex items-center justify-between px-2 py-2 rounded-lg transition-colors",
                  isSelected ? "bg-transparent" : "hover:bg-muted/50"
                )}
              >
                <span className="text-[11px] font-bold text-foreground uppercase">{symbol}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-foreground font-mono">{formatPrice(symbol, price)}</span>
                  <span className={cn(
                    "text-[10px] font-medium w-16 text-right",
                    change >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatChange(change)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============ ORDER BOOK COMPONENTS ============
const AnimatedOrderRow = ({ order, type, isDesktop }: { order: any; type: 'buy' | 'sell'; isDesktop: boolean }) => {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 500);
    return () => clearTimeout(timer);
  }, [order.price, order.total]);
  const textSize = isDesktop ? "text-[10px]" : "text-[2.5vw] sm:text-xs";
  const formatPrice = (price: number) => {
    if (!price || price === 0) return "---";
    if (price < 0.000001) return `$${price.toFixed(10)}`;
    if (price < 0.00001) return `$${price.toFixed(8)}`;
    if (price < 0.001) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };
  return (
    <div className={`flex justify-between ${textSize} transition-all duration-300 ${animate ? (type === 'buy' ? 'translate-y-[-2px] text-emerald-400' : 'translate-y-[2px] text-red-400') : ''}`}>
      <span className={type === 'buy' ? 'text-emerald-500' : 'text-red-500'}>{formatPrice(order.price)}</span>
      <span className="text-muted-foreground">{formatPrice(order.total)}</span>
    </div>
  );
};

const generateSellOrders = (basePrice: number, count: number) => {
  const orders = [];
  const step = basePrice * 0.01;
  for (let i = 1; i <= count; i++) {
    const price = basePrice + (step * i);
    const amount = Math.random() * 1000;
    const total = price * amount;
    orders.push({ price, amount, total });
  }
  return orders.sort((a, b) => a.price - b.price);
};

const generateBuyOrders = (basePrice: number, count: number) => {
  const orders = [];
  const step = basePrice * 0.01;
  for (let i = 1; i <= count; i++) {
    const price = basePrice - (step * i);
    if (price <= 0) continue;
    const amount = Math.random() * 1000;
    const total = price * amount;
    orders.push({ price, amount, total });
  }
  return orders.sort((a, b) => b.price - a.price);
};

// ============ SWAP CARD ============
const SwapCard = ({
  tradeType, setTradeType,
  selectedToken, setSelectedToken,
  amount, setAmount,
  solAmount, setSolAmount,
  quote, quoteLoading, swapLoading,
  currentPrice,
  getTokenBalance,
  handleAmountChange, handleSolAmountChange,
  handleMax, handleSwap,
  showTokenDropdown, setShowTokenDropdown,
  tokenDropdownRef,
  TokenDropdown,
  minimumAmount,
  isDesktop,
  navigate,
}: any) => {
  const cardClassName = isDesktop 
    ? "bg-card border border-border rounded-xl p-6 h-full shadow-sm"
    : "bg-transparent p-0";

  // Get SOL balance
  const solBalance = getTokenBalance("SOL");

  return (
    <div className={cardClassName}>
      {/* Header - Clickable text to navigate to /limit-order */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/limit-order")}
          className="text-[11px] font-bold uppercase tracking-wider text-yellow-500 hover:text-yellow-400 transition-colors cursor-pointer"
        >
          Advanced Trade Execution →
        </button>
      </div>

      {/* BUY/SELL buttons - Simple buttons without toggle card */}
      <div className="flex gap-2 mb-4">
        <button 
          onClick={() => setTradeType("buy")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "buy" 
              ? "bg-yellow-500 text-white border-yellow-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          BUY
        </button>
        <button 
          onClick={() => setTradeType("sell")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "sell" 
              ? "bg-red-500 text-white border-red-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          SELL
        </button>
      </div>

      <div className="space-y-2 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">{tradeType === "buy" ? "BUY" : "SELL"} TOKEN</label>
        <div className="flex gap-2">
          <TokenDropdown value={selectedToken} onChange={setSelectedToken} isOpen={showTokenDropdown} setIsOpen={setShowTokenDropdown} ref={tokenDropdownRef} getTokenBalance={getTokenBalance} />
          <Input type="number" placeholder="0.00" value={amount} onChange={(e) => handleAmountChange(e.target.value)} className="flex-1 text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" disabled={quoteLoading || swapLoading} />
        </div>
        <p className="text-[10px] text-muted-foreground text-right uppercase">Balance: {getTokenBalance(selectedToken).toFixed(6)} {selectedToken}</p>
      </div>

      <div className="space-y-2 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">SOL AMOUNT</label>
        <Input type="number" placeholder="0.00" value={solAmount} onChange={(e) => handleSolAmountChange(e.target.value)} className="w-full text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" disabled={quoteLoading || swapLoading} />
        <p className="text-[10px] text-muted-foreground text-right uppercase">Available: {solBalance.toFixed(6)} SOL</p>
      </div>

      {quoteLoading ? (
        <div className="bg-muted p-3 rounded-lg text-center mb-4"><Loader2 className="w-3 h-3 animate-spin inline mr-1 text-yellow-500" /><span className="text-[8px] text-muted-foreground">Fetching quote...</span></div>
      ) : quote ? (
        <div className="bg-muted p-3 rounded-lg space-y-1 mb-4 border border-border">
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "PAY" : "RECEIVE"}</span>
            <span className="font-medium text-foreground">{quote.inputAmount.toFixed(6)} {tradeType === "buy" ? "SOL" : selectedToken}</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "RECEIVE" : "PAY"}</span>
            <span className="font-medium text-foreground">{quote.outputAmount.toFixed(6)} {tradeType === "buy" ? selectedToken : "SOL"}</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">PRICE</span>
            <span className="font-medium text-foreground">1 {selectedToken} = {currentPrice.toFixed(6)} SOL</span>
          </div>
          {quote.priceImpact !== 0 && (
            <div className="flex justify-between text-[8px]">
              <span className="text-muted-foreground uppercase">PRICE IMPACT</span>
              <span className={cn("font-medium", quote.priceImpact > 2 ? "text-orange-400" : "text-foreground")}>
                {quote.priceImpact.toFixed(2)}%
              </span>
            </div>
          )}
          <div className="flex justify-between text-[8px] border-t border-border pt-1 mt-1">
            <span className="text-muted-foreground uppercase">SLIPPAGE</span>
            <span className={cn(
              "font-medium",
              quote.slippageUsed && quote.slippageUsed > 5 ? "text-orange-400" : 
              quote.slippageUsed && quote.slippageUsed > 2 ? "text-yellow-500" : 
              "text-muted-foreground"
            )}>
              {quote.slippageUsed?.toFixed(1)}%
              {quote.slippageUsed && quote.slippageUsed > DEFAULT_SLIPPAGE_PERCENT && (
                <span className="ml-1 text-yellow-500 text-[8px]">(auto-adjusted)</span>
              )}
            </span>
          </div>
        </div>
      ) : null}

      <Button onClick={handleSwap} disabled={!amount || !solAmount || swapLoading || parseFloat(amount) < getMinimumAmount(selectedToken)} className={cn("w-full h-10 text-[10px] font-bold uppercase", tradeType === "buy" ? "bg-yellow-500 text-white hover:bg-yellow-600" : "bg-red-500 text-white hover:bg-red-600")}>
        {swapLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> PROCESSING...</> : tradeType === "buy" ? `BUY ${selectedToken}` : `SELL ${selectedToken}`}
      </Button>
    </div>
  );
};

const TokenDropdown = React.forwardRef(({ value, onChange, isOpen, setIsOpen, getTokenBalance }: any, ref) => {
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setIsOpen(!isOpen)} className="bg-card border border-border rounded-lg px-3 py-2 text-[10px] font-medium uppercase flex items-center gap-1 min-w-[90px] justify-between hover:bg-muted transition h-9 text-foreground">
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {TOKENS_LIST.map((token) => {
            const balance = getTokenBalance ? getTokenBalance(token) : 0;
            return (
              <button key={token} onClick={() => { onChange(token); setIsOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-muted transition flex items-center justify-between text-[10px]">
                <span className="font-medium uppercase text-foreground">{token}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{balance.toFixed(4)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
TokenDropdown.displayName = "TokenDropdown";

// ============ MARQUEE COMPONENTS ============
const DesktopMarquee = ({ prices }: { prices: Record<string, { price: number; change: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    const priceData = prices[symbol];
    return {
      symbol,
      price: priceData?.price || 0,
      change: priceData?.change || 0,
    };
  }).filter(item => item.price > 0);
  
  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL") {
      return `$${price.toFixed(2)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap border-b border-border py-2">
      <div className="inline-block animate-marquee-desktop">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-4 text-[10px] gap-2">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-desktop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-desktop { animation: marquee-desktop 30s linear infinite; }
      `}</style>
    </div>
  );
};

const MobileMarquee = ({ prices }: { prices: Record<string, { price: number; change: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    const priceData = prices[symbol];
    return {
      symbol,
      price: priceData?.price || 0,
      change: priceData?.change || 0,
    };
  }).filter(item => item.price > 0);
  
  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL") {
      return `$${price.toFixed(2)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap flex-1">
      <div className="inline-block animate-marquee-mobile">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-3 text-[10px] gap-1">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-mobile { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-mobile { animation: marquee-mobile 40s linear infinite; }
      `}</style>
    </div>
  );
};

// ============ SIMPLE NOTIFICATION ============
const SimpleNotification = ({ message, type, onClose }: { message: string; type: "success" | "error" | "info"; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={cn("fixed top-20 right-4 z-[200] p-3 rounded-lg shadow-lg border animate-slide-in", type === "success" && "bg-green-500/90 border-green-400 text-white", type === "error" && "bg-red-500/90 border-red-400 text-white", type === "info" && "bg-blue-500/90 border-blue-400 text-white")}>
      <div className="flex items-center gap-2">
        {type === "success" && <CheckCircle className="w-4 h-4" />}
        {type === "error" && <AlertTriangle className="w-4 h-4" />}
        {type === "info" && <Info className="w-4 h-4" />}
        <p className="text-[10px] font-medium uppercase">{message}</p>
        <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// ============ SWAP SUCCESS DIALOG ============
const SwapSuccessDialog = ({ transactionHash, onClose, tradeType }: { transactionHash: string; onClose: () => void; tradeType: "buy" | "sell" }) => {
  const [copied, setCopied] = useState(false);

  const shortHash = transactionHash ? `${transactionHash.slice(0, 8)}...${transactionHash.slice(-8)}` : '';

  const handleCopyHash = () => {
    navigator.clipboard.writeText(transactionHash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewOnExplorer = () => {
    window.open(`https://solscan.io/tx/${transactionHash}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-green-500/50 rounded-xl p-6 w-full max-w-sm space-y-4 shadow-lg">
        <div className="flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-lg font-bold uppercase text-green-500">
            {tradeType === "buy" ? "BUY" : "SELL"} SUCCESSFUL
          </h2>
          <p className="text-[10px] text-muted-foreground uppercase">Transaction completed on Solana</p>
        </div>

        <div className="bg-muted rounded-lg p-4 space-y-2 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase">Transaction Hash</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[10px] font-mono text-yellow-500 break-all">{shortHash}</code>
            <button
              onClick={handleCopyHash}
              className={cn(
                "p-2 rounded transition-colors",
                copied ? "bg-green-500/20 text-green-400" : "bg-muted hover:bg-muted/80 text-muted-foreground"
              )}
              title={copied ? "Copied!" : "Copy"}
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
          <p className="text-[8px] text-muted-foreground">Full: {transactionHash}</p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleViewOnExplorer}
            className="flex-1 bg-yellow-500 text-white hover:bg-yellow-600 h-9 text-[10px] font-bold uppercase flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-3 h-3" />
            View on Solscan
          </Button>
          <Button
            onClick={onClose}
            className="flex-1 bg-green-500 text-white hover:bg-green-600 h-9 text-[10px] font-bold uppercase"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ BACKUP FILE DIALOG ============
const BackupFileDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [manualBackupText, setManualBackupText] = useState("");
  const [manualError, setManualError] = useState("");

  if (!show) return null;

  const validateBackupContent = (content: string): boolean => {
    if (content.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
    const parsed = JSON.parse(content);
    if (!parsed.magic || !parsed.salt || !parsed.iv || !parsed.data) throw new Error("Invalid backup file structure.");
    if (parsed.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format.");
    return true;
  };

  const handleManualSubmit = () => {
    if (!manualBackupText.trim()) {
      setManualError("Please paste your backup JSON");
      return;
    }
    try {
      validateBackupContent(manualBackupText);
      onSubmit(manualBackupText);
      setManualBackupText("");
      setManualError("");
    } catch (err: any) {
      setManualError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg">
        {(error || manualError) && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">{error || manualError}</div>}
        
        <div className="space-y-3">
          <textarea
            value={manualBackupText}
            onChange={(e) => setManualBackupText(e.target.value)}
            placeholder='Paste your backup Key here...'
            className="w-full h-32 bg-muted border border-border rounded-lg p-2 text-[8px] font-mono text-foreground resize-none focus:outline-none focus:border-yellow-500"
          />
          <Button onClick={handleManualSubmit} disabled={loading} className="w-full bg-yellow-500 text-white hover:bg-yellow-600 text-sm">
            Verify & Use
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN SWAP COMPONENT ============
export default function Swap() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenBalances, setTokenBalances] = useState<any[]>([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [isMPCUser, setIsMPCUser] = useState(false);

  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [selectedToken, setSelectedToken] = useState<string>("SOL");
  const [amount, setAmount] = useState<string>("");
  const [solAmount, setSolAmount] = useState<string>("");
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [swapLoading, setSwapLoading] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, password: "", showPassword: false });
  const [notification, setNotification] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [pendingSwap, setPendingSwap] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [processingSwap, setProcessingSwap] = useState(false);
  const [swapSuccessDialog, setSwapSuccessDialog] = useState<{ show: boolean; transactionHash: string } | null>(null);

  const tokenDropdownRef = useRef<HTMLDivElement>(null);
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const tokenAddressesList = useMemo(() => TOKENS_LIST.map(token => TOKEN_ADDRESSES[token]), []);
  const { prices: dexPrices, loading: pricesLoading } = useMultipleTokenPrices(tokenAddressesList, { refetchInterval: 30000 });

  // Get prices for display tokens (SOL, FIXERCOIN, LOCKER, PINGX, FXM)
  const displayAddresses = useMemo(() => DISPLAY_TOKENS_LIST.map(symbol => TOKEN_ADDRESSES[symbol]), []);
  const { prices: displayPriceData, loading: displayLoading } = useMultipleTokenPrices(displayAddresses, { refetchInterval: 30000 });

  const displayPrices = useMemo(() => {
    const result: Record<string, { price: number; change: number }> = {};
    DISPLAY_TOKENS_LIST.forEach(symbol => {
      const address = TOKEN_ADDRESSES[symbol];
      const priceData = displayPriceData[address];
      if (priceData && priceData.priceUsd > 0) {
        result[symbol] = {
          price: priceData.priceUsd,
          change: priceData.priceChange24h || 0,
        };
      } else {
        result[symbol] = { price: 0, change: 0 };
      }
    });
    return result;
  }, [displayPriceData]);

  const tokenPrices = useMemo(() => {
    const mappedPrices: Record<string, number> = {};
    TOKENS_LIST.forEach(token => {
      if (token === "SOL") {
        mappedPrices[token] = 1;
        return;
      }
      const address = TOKEN_ADDRESSES[token];
      const dexPrice = dexPrices[address]?.priceUsd;
      const solPrice = dexPrices[TOKEN_ADDRESSES.SOL]?.priceUsd || 1;
      if (dexPrice && dexPrice > 0 && !isNaN(dexPrice)) {
        mappedPrices[token] = dexPrice / solPrice;
      } else {
        mappedPrices[token] = 0;
      }
    });
    return mappedPrices;
  }, [dexPrices]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            setIsMPCUser(data.user.isMPC === true);
            setLoading(false);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Swap] Auth error:", error);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const solanaWalletAddress = user?.wallets?.solana || null;
  const { balances: solanaBalances, refetch: refetchBalances } = useSolanaBalances({
    walletAddress: solanaWalletAddress || "",
    refetchInterval: false,
    enabled: !!solanaWalletAddress,
  });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadBalances = async () => {
      if (!solanaWalletAddress) return;
      
      const balances: any[] = [];
      for (const symbol of TOKENS_LIST) {
        const balance = solanaBalances?.[symbol] || 0;
        balances.push({ symbol, balance });
      }
      if (balances.length > 0) setTokenBalances(balances);
    };
    loadBalances();
  }, [solanaBalances, solanaWalletAddress]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target as Node)) setShowTokenDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
    };
  }, []);

  const getTokenBalance = (symbol: string): number => {
    const token = tokenBalances.find(t => t.symbol === symbol);
    return token?.balance || 0;
  };
  
  const currentPrice = tokenPrices[selectedToken] || 0;
  const solPrice = dexPrices[TOKEN_ADDRESSES.SOL]?.priceUsd || 180;
  const minimumAmountForToken = getMinimumAmount(selectedToken);

  const handleAmountChange = (value: string) => {
    setAmount(value);
    if (value && currentPrice > 0) {
      const calculatedSol = parseFloat(value) * currentPrice;
      setSolAmount(calculatedSol.toFixed(6));
    } else {
      setSolAmount("");
    }
  };

  const handleSolAmountChange = (value: string) => {
    setSolAmount(value);
    if (value && currentPrice > 0) {
      const calculatedAmount = parseFloat(value) / currentPrice;
      setAmount(calculatedAmount.toFixed(6));
    } else {
      setAmount("");
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedToken || currentPrice === 0) { setQuote(null); return; }
      if (selectedToken === "SOL") { setQuote(null); return; }
      if (tradeType === "sell" && parseFloat(amount) < minimumAmountForToken) { setQuote(null); return; }
      if (tradeType === "buy" && parseFloat(solAmount) < 0.000001) { setQuote(null); return; }
      setQuoteLoading(true);
      try {
        const fromMint = tradeType === "buy" ? SOL_ADDRESS : TOKEN_ADDRESSES[selectedToken];
        const toMint = tradeType === "buy" ? TOKEN_ADDRESSES[selectedToken] : SOL_ADDRESS;
        let quantityInSmallestUnits: number;
        if (tradeType === "buy") {
          const solValue = parseFloat(solAmount);
          if (isNaN(solValue) || solValue <= 0) { setQuote(null); return; }
          const solDecimals = TOKEN_DECIMALS.SOL || 9;
          quantityInSmallestUnits = Math.floor(solValue * Math.pow(10, solDecimals));
        } else {
          const tokenAmount = parseFloat(amount);
          if (isNaN(tokenAmount) || tokenAmount <= 0) { setQuote(null); return; }
          const decimals = TOKEN_DECIMALS[selectedToken] || 9;
          quantityInSmallestUnits = Math.floor(tokenAmount * Math.pow(10, decimals));
        }
        if (quantityInSmallestUnits < MIN_SMALLEST_UNITS) { setQuote(null); return; }
        
        const response = await fetch("/api/dflow", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ 
            action: "quote", 
            params: { 
              inputMint: fromMint, 
              outputMint: toMint, 
              amount: quantityInSmallestUnits.toString(), 
              slippageBps: DEFAULT_SLIPPAGE_BPS.toString() 
            } 
          }),
        });
        if (!response.ok) throw new Error("Failed to get quote");
        const quoteData = await response.json();
        if (!quoteData.outAmount || parseFloat(quoteData.outAmount) === 0) throw new Error("No valid quote");
        
        const outputDecimals = tradeType === "buy" ? (TOKEN_DECIMALS[selectedToken] || 9) : 9;
        const inputDecimals = tradeType === "buy" ? 9 : (TOKEN_DECIMALS[selectedToken] || 9);
        const outputAmount = parseFloat(quoteData.outAmount) / Math.pow(10, outputDecimals);
        const inputAmount = parseFloat(quoteData.inAmount) / Math.pow(10, inputDecimals);
        
        const slippageUsed = quoteData._slippageBps ? quoteData._slippageBps / 100 : DEFAULT_SLIPPAGE_PERCENT;
        
        if (slippageUsed > DEFAULT_SLIPPAGE_PERCENT) {
          console.log(`[Swap] ⚠️ Auto-slippage adjusted to ${slippageUsed}% for ${selectedToken}`);
        }
        
        setQuote({ 
          inputAmount, 
          outputAmount, 
          price: currentPrice, 
          priceImpact: parseFloat(quoteData.priceImpactPct) || 0,
          slippageUsed 
        });
      } catch (error: any) {
        console.error("[Swap] Quote error:", error);
        setQuote(null);
        const errorMsg = error.message?.replace(/<[^>]*>/g, '').substring(0, 200) || "Unable to get quote";
        if (errorMsg !== "Network request failed") {
          toast({ title: "Quote Failed", description: errorMsg, variant: "destructive" });
        }
      } finally { setQuoteLoading(false); }
    };
    const debounceTimer = setTimeout(fetchQuote, 800);
    return () => clearTimeout(debounceTimer);
  }, [amount, solAmount, selectedToken, tradeType, currentPrice, minimumAmountForToken]);

  // ============ EXECUTE MARKET SWAP ============
  const executeMarketSwap = async (password: string) => {
    if (!user || !amount || parseFloat(amount) <= 0) { 
      setNotification({ message: "Please enter a valid amount", type: "error" }); 
      setSwapLoading(false); 
      setProcessingSwap(false); 
      return; 
    }
    if (selectedToken === "SOL") { 
      setNotification({ message: "Cannot swap SOL for SOL", type: "error" }); 
      setSwapLoading(false); 
      setProcessingSwap(false); 
      return; 
    }
    if (tradeType === "sell" && parseFloat(amount) < minimumAmountForToken) { 
      setNotification({ message: `Minimum sell amount is ${minimumAmountForToken.toFixed(6)} ${selectedToken}`, type: "error" }); 
      setSwapLoading(false); 
      setProcessingSwap(false); 
      return; 
    }
    if (tradeType === "buy" && parseFloat(solAmount) < 0.000001) { 
      setNotification({ message: "Minimum buy amount is 0.000001 SOL", type: "error" }); 
      setSwapLoading(false); 
      setProcessingSwap(false); 
      return; 
    }

    let feeAmount: number;
    let netAmount: number;
    let feeToken: string;
    let feeTokenDecimals: number;
    let feeAddress: string;

    if (tradeType === "buy") {
      const tokenAmount = parseFloat(amount);
      feeAmount = tokenAmount * PLATFORM_FEE_PERCENT / 100;
      netAmount = tokenAmount - feeAmount;
      feeToken = selectedToken;
      feeTokenDecimals = TOKEN_DECIMALS[selectedToken] || 9;
      feeAddress = FEE_ADDRESS;
    } else {
      const tokenAmount = parseFloat(amount);
      feeAmount = tokenAmount * PLATFORM_FEE_PERCENT / 100;
      netAmount = tokenAmount - feeAmount;
      feeToken = selectedToken;
      feeTokenDecimals = TOKEN_DECIMALS[selectedToken] || 9;
      feeAddress = FEE_ADDRESS;
    }

    if (feeAmount < 0.00000001) {
      setNotification({ message: "Amount too small after fee deduction", type: "error" });
      setSwapLoading(false);
      setProcessingSwap(false);
      return;
    }

    if (tradeType === "sell") {
      const availableBalance = getTokenBalance(selectedToken);
      if (availableBalance < parseFloat(amount)) {
        setNotification({
          message: `Insufficient available balance. Available: ${availableBalance.toFixed(6)} ${selectedToken}`,
          type: "error"
        });
        setSwapLoading(false);
        setProcessingSwap(false);
        return;
      }
    }

    setSwapLoading(true);
    try {
      const fromMint = tradeType === "buy" ? SOL_ADDRESS : TOKEN_ADDRESSES[selectedToken];
      const toMint = tradeType === "buy" ? TOKEN_ADDRESSES[selectedToken] : SOL_ADDRESS;
      
      let quantityInSmallestUnits: number;
      let originalTokenAmount: number;
      if (tradeType === "buy") {
        const solValue = parseFloat(solAmount);
        const solDecimals = TOKEN_DECIMALS.SOL || 9;
        quantityInSmallestUnits = Math.floor(solValue * Math.pow(10, solDecimals));
        originalTokenAmount = parseFloat(amount);
      } else {
        originalTokenAmount = parseFloat(amount);
        const decimals = TOKEN_DECIMALS[selectedToken] || 9;
        quantityInSmallestUnits = Math.floor(netAmount * Math.pow(10, decimals));
      }
      if (quantityInSmallestUnits < MIN_SMALLEST_UNITS) throw new Error("Amount too small after fee deduction");

      let finalQuantityInSmallestUnits = quantityInSmallestUnits;
      let quoteData = null;

      const quoteResponse = await fetch("/api/dflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          action: "quote", 
          params: { 
            inputMint: fromMint, 
            outputMint: toMint, 
            amount: finalQuantityInSmallestUnits.toString(), 
            slippageBps: DEFAULT_SLIPPAGE_BPS.toString() 
          } 
        }),
      });
      
      if (!quoteResponse.ok) {
        const errorText = await quoteResponse.text();
        throw new Error(`Quote failed: ${quoteResponse.status} - ${errorText}`);
      }
      
      quoteData = await quoteResponse.json();
      if (!quoteData.outAmount || parseFloat(quoteData.outAmount) === 0) {
        throw new Error("DFlow returned no valid quote");
      }

      if (quoteData._slippageBps) {
        console.log(`[Swap] Quote successful with ${quoteData._slippageBps / 100}% slippage`);
      }

      if (tradeType === "buy") {
        const quotedTokenAmount = Number(quoteData.outAmount) / Math.pow(10, feeTokenDecimals);
        feeAmount = quotedTokenAmount * PLATFORM_FEE_PERCENT / 100;
      }

      const swapResponse = await fetch("/api/dflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          action: "swap", 
          params: { 
            quoteResponse: quoteData, 
            userPublicKey: solanaWalletAddress 
          } 
        }),
      });
      
      if (!swapResponse.ok) {
        const errorText = await swapResponse.text();
        throw new Error(`Swap failed: ${swapResponse.status} - ${errorText}`);
      }
      
      const swapData = await swapResponse.json();
      if (!swapData.swapTransaction) {
        throw new Error("No swap transaction in response");
      }
      
      const feeInSmallestUnits = Math.floor(feeAmount * Math.pow(10, feeTokenDecimals));
      const swapPayload: any = {
        userId: user.email,
        password,
        type: "market_swap",
        fromMint,
        toMint,
        quantity: finalQuantityInSmallestUnits,
        swapTransaction: swapData.swapTransaction,
        quoteResponse: quoteData,
        feeAmount: feeInSmallestUnits,
        feeToken,
        feeAddress,
        originalTokenAmount: originalTokenAmount,
      };
      if (isMPCUser && backupFileContent) swapPayload.backupFile = backupFileContent;
      
      const response = await fetch("/api/swap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(swapPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          if (result.needsBackup) throw new Error("MPC backup file required. Please upload your backup file.");
          if (result.needsPassword) throw new Error("Password required to decrypt wallet");
          throw new Error(result.error || "Invalid credentials");
        }
        throw new Error(result.error || "Swap failed");
      }

      if (response.status === 202 || result.transactionBroadcast) {
        const transactionHash = extractHashFromMessage(result.error || result.message || "");
        setNotification({
          message: transactionHash
            ? `Transaction ${transactionHash.slice(0, 8)}… is pending confirmation. Check Solscan before retrying.`
            : "Transaction broadcast but is still pending confirmation. Check Solscan before retrying.",
          type: "info",
        });
        return;
      }

      if (!result.success || !result.transactionHash) {
        throw new Error("Swap did not return a confirmed transaction hash");
      }

      setSwapSuccessDialog({ show: true, transactionHash: result.transactionHash });
      setAmount("");
      setSolAmount("");
      setQuote(null);
      await refetchBalances();
      
    } catch (error: any) {
      console.error("[Swap] Error in executeMarketSwap:", error);
      throw error;
    } finally {
      setSwapLoading(false);
      setProcessingSwap(false);
    }
  };

  const handleSwapError = (error: any) => {
    console.error("[Swap] Error:", error);
    const errorMsg = error.message || '';
    if (errorMsg.includes('MPC backup file required') || errorMsg.includes('MPC') || errorMsg.includes('backup')) {
      setShowBackupDialog(true);
      setNotification({ message: "Backup file is required. Please paste your backup file.", type: "error" });
    } else if (errorMsg.includes('Invalid credentials') || errorMsg.includes('Password')) {
      setNotification({ message: "Invalid password. Please try again.", type: "error" });
      setPasswordDialog({ ...passwordDialog, open: true, password: "" });
    } else {
      setNotification({ message: errorMsg.replace(/<[^>]*>/g, '').substring(0, 200) || "Swap failed", type: "error" });
    }
  };

  const handleSwap = async (password?: string) => {
    if (!user) { setNotification({ message: "User not found", type: "error" }); return; }

    if (isMPCUser && !backupFileContent) { 
      setPendingSwap(true); 
      setShowBackupDialog(true); 
      return; 
    }
    if (!password) { 
      setPasswordDialog({ ...passwordDialog, open: true }); 
      return; 
    }

    setPasswordDialog({ open: false, password: "", showPassword: false });
    setSwapLoading(true);
    setProcessingSwap(true);

    try {
      await executeMarketSwap(password);
    } catch (error: any) {
      handleSwapError(error);
    }
  };

  const handleMax = () => {
    const balance = getTokenBalance(selectedToken);
    setAmount(balance.toString());
    setSolAmount((balance * currentPrice).toFixed(6));
  };

  const handleBackupSubmit = (backupContent: string) => {
    setBackupFileContent(backupContent);
    setShowBackupDialog(false);
    setNotification({ message: "Backup file loaded successfully!", type: "success" });
    if (pendingSwap) { setPendingSwap(false); setTimeout(() => setPasswordDialog({ ...passwordDialog, open: true }), 100); }
  };

  const handleCancelBackup = () => {
    setShowBackupDialog(false);
    setPendingSwap(false);
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  const goToConvert = () => navigate("/convert");
  const goToLimitOrders = () => navigate("/limit-order");
  const goToTransfer = () => navigate("/transfer");

  if (isDesktop) {
    return (
      <div className="desktop-dark min-h-screen bg-background text-foreground px-4 pb-4 pt-0">
        {/* Desktop-only dark black theme override — layout untouched */}
        <style>{`
          .desktop-dark {
            --background: 0 0% 0%;
            --card: 240 10% 4%;
            --popover: 240 10% 4%;
            --border: 240 4% 16%;
            --input: 240 4% 16%;
            --muted: 240 4% 12%;
            --foreground: 0 0% 98%;
            --muted-foreground: 240 5% 65%;
          }
        `}</style>
        <DesktopMarquee prices={displayPrices} />
        <div className="flex gap-2 h-[calc(100vh-2rem)] mt-4">
          {/* Left Column - Token Prices (25%) */}
          <div className="w-[25%]">
            <TokenPriceList 
              prices={displayPrices} 
              onSelectToken={(symbol: string) => {
                setSelectedToken(symbol);
                setAmount("");
                setSolAmount("");
                setQuote(null);
              }}
              selectedToken={selectedToken}
            />
          </div>
          
          {/* Center Column - Chart (50%) */}
          <div className="w-[50%]">
            <BirdeyeChart 
              tokenAddress={TOKEN_ADDRESSES[selectedToken] || TOKEN_ADDRESSES.SOL} 
              chain="solana"
            />
          </div>
          
          {/* Right Column - Swap Form (25%) */}
          <div className="w-[25%]">
            <SwapCard
              tradeType={tradeType} setTradeType={setTradeType}
              selectedToken={selectedToken} setSelectedToken={setSelectedToken}
              amount={amount} setAmount={setAmount}
              solAmount={solAmount} setSolAmount={setSolAmount}
              quote={quote} quoteLoading={quoteLoading} swapLoading={swapLoading}
              currentPrice={currentPrice}
              getTokenBalance={getTokenBalance}
              handleAmountChange={handleAmountChange} handleSolAmountChange={handleSolAmountChange}
              handleMax={handleMax} handleSwap={() => handleSwap()}
              showTokenDropdown={showTokenDropdown} setShowTokenDropdown={setShowTokenDropdown}
              tokenDropdownRef={tokenDropdownRef} TokenDropdown={TokenDropdown}
              minimumAmount={minimumAmountForToken}
              isDesktop={true}
              navigate={navigate}
            />
          </div>
        </div>
        {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
        {passwordDialog.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
              <h2 className="text-base font-bold uppercase text-foreground">CONFIRM {tradeType === "buy" ? "BUY" : "SELL"} ORDER</h2>
              <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO EXECUTE SWAP</p>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
                <div className="relative">
                  <Input type={passwordDialog.showPassword ? "text" : "password"} placeholder="ENTER PASSWORD" value={passwordDialog.password} onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} disabled={swapLoading} onKeyPress={(e) => e.key === "Enter" && handleSwap(passwordDialog.password)} className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" />
                  <button onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground">SHOW</button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} disabled={swapLoading} variant="outline" className="flex-1 text-xs border-border text-foreground hover:bg-muted">CANCEL</Button>
                <Button onClick={() => handleSwap(passwordDialog.password)} disabled={!passwordDialog.password || swapLoading} className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}>{swapLoading ? "PROCESSING..." : "CONFIRM"}</Button>
              </div>
            </div>
          </div>
        )}
        {processingSwap && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card border border-yellow-500/50 rounded-xl p-6 flex items-center gap-3 shadow-lg">
              <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
              <span className="text-sm font-medium uppercase text-yellow-500">Processing swap...</span>
            </div>
          </div>
        )}
        {swapSuccessDialog?.show && (
          <SwapSuccessDialog
            transactionHash={swapSuccessDialog.transactionHash}
            onClose={() => setSwapSuccessDialog({ show: false, transactionHash: '' })}
            tradeType={tradeType}
          />
        )}
        <BackupFileDialog show={showBackupDialog} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error="" loading={false} />
      </div>
    );
  }

  // Mobile layout - with marquee, no 3-dot dropdown, no order book
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Mobile Marquee */}
      <div className="border-b border-border bg-card py-1">
        <MobileMarquee prices={displayPrices} />
      </div>
      <div className="p-4">
        <SwapCard
          tradeType={tradeType} setTradeType={setTradeType}
          selectedToken={selectedToken} setSelectedToken={setSelectedToken}
          amount={amount} setAmount={setAmount}
          solAmount={solAmount} setSolAmount={setSolAmount}
          quote={quote} quoteLoading={quoteLoading} swapLoading={swapLoading}
          currentPrice={currentPrice}
          getTokenBalance={getTokenBalance}
          handleAmountChange={handleAmountChange} handleSolAmountChange={handleSolAmountChange}
          handleMax={handleMax} handleSwap={() => handleSwap()}
          showTokenDropdown={showTokenDropdown} setShowTokenDropdown={setShowTokenDropdown}
          tokenDropdownRef={tokenDropdownRef} TokenDropdown={TokenDropdown}
          minimumAmount={minimumAmountForToken}
          isDesktop={false}
          navigate={navigate}
        />
      </div>
      {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      {passwordDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
            <h2 className="text-base font-bold uppercase text-foreground">CONFIRM {tradeType === "buy" ? "BUY" : "SELL"} ORDER</h2>
            <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO EXECUTE SWAP</p>
            <div className="space-y-1">
              <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
              <div className="relative">
                <Input type={passwordDialog.showPassword ? "text" : "password"} placeholder="ENTER PASSWORD" value={passwordDialog.password} onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} disabled={swapLoading} onKeyPress={(e) => e.key === "Enter" && handleSwap(passwordDialog.password)} className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" />
                <button onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground">SHOW</button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} disabled={swapLoading} variant="outline" className="flex-1 text-xs border-border text-foreground hover:bg-muted">CANCEL</Button>
              <Button onClick={() => handleSwap(passwordDialog.password)} disabled={!passwordDialog.password || swapLoading} className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}>{swapLoading ? "PROCESSING..." : "CONFIRM"}</Button>
            </div>
          </div>
        </div>
      )}
      {processingSwap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-yellow-500/50 rounded-xl p-6 flex items-center gap-3 shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
            <span className="text-sm font-medium uppercase text-yellow-500">Processing swap...</span>
          </div>
        </div>
      )}
      {swapSuccessDialog?.show && (
        <SwapSuccessDialog
          transactionHash={swapSuccessDialog.transactionHash}
          onClose={() => setSwapSuccessDialog({ show: false, transactionHash: '' })}
          tradeType={tradeType}
        />
      )}
      <BackupFileDialog show={showBackupDialog} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error="" loading={false} />
    </div>
  );
}
